import React, { useEffect, useState } from 'react';
import { Calendar, Loader2, DollarSign, X, Send, CheckCircle2, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { comisionesApi, edificiosApi } from '../../../lib/api';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../../components/ui/select';
import { cn } from '../../../lib/utils';

const formatMoney = (monto, moneda = 'ARS') => {
  const n = Number(monto || 0);
  const simbolo = moneda === 'USD' ? 'US$' : '$';
  return `${simbolo} ${new Intl.NumberFormat('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n)}`;
};

const formatDate = (s) => {
  if (!s) return '-';
  try { return new Date(s + 'T00:00:00').toLocaleDateString('es-AR'); } catch { return s; }
};

// Celda editable del porcentaje de comisión
const PorcentajeCell = ({ comision, onUpdated, disabled }) => {
  const [value, setValue] = useState(String(comision.porcentaje ?? 50));
  const [saving, setSaving] = useState(false);

  useEffect(() => { setValue(String(comision.porcentaje ?? 50)); }, [comision.porcentaje]);

  const save = async () => {
    const num = parseFloat(value);
    if (isNaN(num) || num < 0 || num > 100) {
      setValue(String(comision.porcentaje ?? 50));
      return;
    }
    if (num === comision.porcentaje) return;
    setSaving(true);
    try {
      await comisionesApi.update(comision.comision_id, { porcentaje: num });
      onUpdated && onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar %');
      setValue(String(comision.porcentaje ?? 50));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="inline-flex items-center gap-1 justify-end">
      <Input
        type="number"
        min="0"
        max="100"
        step="0.01"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') setValue(String(comision.porcentaje ?? 50));
        }}
        disabled={disabled || saving}
        className="h-7 w-16 text-right text-sm px-1"
        data-testid={`porcentaje-input-${comision.comision_id}`}
      />
      <span className="text-xs text-slate-400">%</span>
    </div>
  );
};

// Celda del estado con acciones según rol/estado
const EstadoCell = ({ comision, onUpdated }) => {
  const [working, setWorking] = useState(false);

  const handleSolicitar = async () => {
    setWorking(true);
    try {
      await comisionesApi.solicitar(comision.comision_id);
      toast.success('Cobro solicitado');
      onUpdated && onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al solicitar');
    } finally {
      setWorking(false);
    }
  };

  const handleDeshacer = async () => {
    if (!window.confirm('¿Deshacer la solicitud de cobro? La comisión vuelve a "No solicitada".')) return;
    setWorking(true);
    try {
      await comisionesApi.deshacer(comision.comision_id);
      toast.success('Solicitud de cobro deshecha');
      onUpdated && onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al deshacer');
    } finally {
      setWorking(false);
    }
  };

  if (comision.estado === 'cobrada') {
    return (
      <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-green-100 text-green-700 inline-flex items-center gap-1">
        <CheckCircle2 className="w-3 h-3" />
        Cobrada
      </span>
    );
  }

  if (comision.estado === 'pendiente') {
    return (
      <div className="inline-flex items-center gap-1.5">
        <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700">
          Pendiente
        </span>
        <button
          onClick={handleDeshacer}
          disabled={working}
          className="p-1 rounded text-slate-500 hover:bg-slate-100 hover:text-red-600 disabled:opacity-40"
          title="Deshacer solicitud de cobro"
          data-testid={`deshacer-cobro-${comision.comision_id}`}
        >
          <Undo2 className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  // no_solicitada
  return (
    <Button
      size="sm"
      variant="outline"
      className="h-7 text-xs bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
      onClick={handleSolicitar}
      disabled={working}
      data-testid={`solicitar-cobro-${comision.comision_id}`}
    >
      <Send className="w-3 h-3 mr-1" />
      {working ? 'Solicitando...' : 'Solicitar cobro'}
    </Button>
  );
};

export const TabComisiones = ({ edificioId }) => {
  const [loading, setLoading] = useState(true);
  const [comisiones, setComisiones] = useState([]);
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [filtroEdificio, setFiltroEdificio] = useState(edificioId || 'todos');
  const [edificiosList, setEdificiosList] = useState([]);
  const [busqueda, setBusqueda] = useState('');

  // Cargar lista de edificios para el selector
  useEffect(() => {
    (async () => {
      try {
        const res = await edificiosApi.list();
        setEdificiosList(res.data || []);
      } catch (err) {
        console.error('Error cargando edificios:', err);
      }
    })();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filtroEdificio && filtroEdificio !== 'todos') params.edificio_id = filtroEdificio;
      if (filtroEstado !== 'todos') params.estado = filtroEstado;

      const lstRes = await comisionesApi.list(params);
      setComisiones(lstRes.data || []);
    } catch (err) {
      console.error(err);
      toast.error('Error al cargar comisiones');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroEdificio, filtroEstado]);

  // Filtrado local por texto (unidad, inquilino, edificio)
  const comisionesFiltradas = comisiones.filter((c) => {
    if (!busqueda.trim()) return true;
    const q = busqueda.trim().toLowerCase();
    return (
      (c.edificio_nombre || '').toLowerCase().includes(q) ||
      (c.unidad_nombre || '').toLowerCase().includes(q) ||
      (c.inquilino || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-4">
      {/* Header con título + filtros en la misma fila */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-3 mr-auto">
          <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
            <DollarSign className="w-5 h-5 text-emerald-700" />
          </div>
          <h1 className="text-2xl font-semibold text-slate-900">Comisiones</h1>
        </div>

        <Select value={filtroEdificio} onValueChange={setFiltroEdificio}>
          <SelectTrigger className="h-9 w-48" data-testid="filtro-edificio-comisiones"><SelectValue placeholder="Edificio" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los edificios</SelectItem>
            {edificiosList.map((ed) => (
              <SelectItem key={ed.edificio_id} value={ed.edificio_id}>{ed.nombre}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filtroEstado} onValueChange={setFiltroEstado}>
          <SelectTrigger className="h-9 w-40" data-testid="filtro-estado-comisiones"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todas</SelectItem>
            <SelectItem value="no_solicitada">Sin solicitar</SelectItem>
            <SelectItem value="pendiente">Pendientes</SelectItem>
            <SelectItem value="cobrada">Cobradas</SelectItem>
          </SelectContent>
        </Select>

        <Input
          type="text"
          className="h-9 w-64"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="Buscar por edificio, unidad o inquilino..."
          data-testid="buscador-comisiones"
        />

        {(busqueda || filtroEstado !== 'todos' || filtroEdificio !== 'todos') && (
          <Button variant="ghost" size="sm" className="h-9" onClick={() => { setBusqueda(''); setFiltroEstado('todos'); setFiltroEdificio('todos'); }}>
            <X className="w-4 h-4 mr-1" /> Limpiar
          </Button>
        )}
      </div>

      {/* Tabla */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto" data-testid="tabla-comisiones">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs text-slate-500 uppercase">
                <th className="py-2 px-3 font-medium">Edificio</th>
                <th className="py-2 px-3 font-medium">Unidad</th>
                <th className="py-2 px-3 font-medium">Inquilino</th>
                <th className="py-2 px-3 font-medium">Firma</th>
                <th className="py-2 px-3 font-medium text-right">Alquiler</th>
                <th className="py-2 px-3 font-medium text-right">% Comisión</th>
                <th className="py-2 px-3 font-medium text-right">Comisión</th>
                <th className="py-2 px-3 font-medium">Cobro</th>
                <th className="py-2 px-3 font-medium">Fecha</th>
              </tr>
            </thead>
            <tbody>
              {comisionesFiltradas.length === 0 ? (
                <tr><td colSpan={9} className="text-center text-slate-400 py-10">No hay comisiones para los filtros seleccionados.</td></tr>
              ) : (
                comisionesFiltradas.map((c) => (
                  <tr key={c.comision_id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`comision-row-${c.comision_id}`}>
                    <td className="py-2 px-3 text-slate-600">{c.edificio_nombre || '—'}</td>
                    <td className="py-2 px-3 text-slate-600">{c.unidad_nombre || '—'}</td>
                    <td className="py-2 px-3 font-medium text-slate-800">{c.inquilino || '—'}</td>
                    <td className="py-2 px-3 text-slate-600 whitespace-nowrap">
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="w-3 h-3 text-slate-400" />
                        {formatDate(c.fecha_firma)}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-right text-slate-600">{formatMoney(c.monto_alquiler_inicial, c.moneda)}</td>
                    <td className="py-2 px-3 text-right">
                      <PorcentajeCell comision={c} onUpdated={fetchData} disabled={c.estado === 'cobrada' || c.estado === 'pendiente'} />
                    </td>
                    <td className="py-2 px-3 text-right font-semibold text-slate-900">{formatMoney(c.monto_comision, c.moneda)}</td>
                    <td className="py-2 px-3">
                      <EstadoCell comision={c} onUpdated={fetchData} />
                    </td>
                    <td className="py-2 px-3 text-slate-600 whitespace-nowrap text-xs">
                      {c.estado === 'cobrada' && c.fecha_cobro ? (
                        <span className="inline-flex items-center gap-1 text-green-700">
                          <Calendar className="w-3 h-3" /> {formatDate(c.fecha_cobro)}
                        </span>
                      ) : c.estado === 'pendiente' && c.fecha_solicitud ? (
                        <span className="inline-flex items-center gap-1 text-amber-700">
                          <Calendar className="w-3 h-3" /> {formatDate(c.fecha_solicitud)}
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TabComisiones;
